// DataTable.jsx — PRIMO componente condiviso del design system (F2.3b).
// Tabella dati con gli STATI OBBLIGATORI del gestionale (regole UI 4):
//   loading  → spinner + 'Caricamento…' (mai schermo che finge)
//   error    → messaggio + bottone 'Riprova' (callback onRetry del chiamante)
//   empty    → messaggio DEDICATO (emptyMessage), mai tabella vuota muta
//   data     → righe; con righe presenti il reload in corso/fallito NON svuota
//              la lista (precedenza in data-table-core.selectTableState, testata).
//
// API (pensata per riuso, mandato ondata 12):
//   columns  [{ key, label, render?(row, ctx), align?('left'|'center'|'right'),
//               sortable?, sortValue?(row), filter?:false, filterText?(row) }]
//   rows     array di oggetti riga
//   rowKey   campo (stringa) o funzione riga→chiave; fallback indice nel core
//   onRowClick?(row)   riga cliccabile (hover + cursor solo se presente)
//   sort?    ordinamento INIZIALE { key, dir } — il toggle al click vive solo
//            sulle colonne sortable:true (nextSort del core)
//   loading, error (stringa|Error), onRetry?, emptyMessage
//
// ESTENSIONI F2.3c (proc-v3 Fornitori) — tutte dietro prop NUOVE, consumatori
// esistenti (sop-v3, dash-v3) INVARIATI:
//   filterable   → riga di filtri PER COLONNA sotto l'header (semantica v1:
//                  virgola = OPPURE tra gruppi, spazi = E tra token — logica
//                  PURA in data-table-core.filterRows/parseFilterQuery),
//                  evidenziazione <mark> dei token (splitByTokens, i più lunghi
//                  vincono), footer "X di Y righe." e — a filtro che esclude
//                  tutto — tabella con riga-messaggio (parità makeDataTable v1).
//                  col.filter === false esclude la colonna; col.filterText(row)
//                  è il testo di match (nel v1: stripHtml(render(row))).
//   selectable   → checkbox per riga + "seleziona tutti i filtrati" + barra bulk
//                  ("N selezionati" + azioni + Deseleziona). La selezione vive nel
//                  Set ESTERNO `selection` (persistente tra i re-render, come il
//                  supplierSelection del v1); bulkActions [{label, fn(chosen)}]
//                  con resolveSelection(ids)→righe (include le selezionate fuori
//                  filtro, parità v1:10971) e GUARDIA anti doppio-click (ref
//                  sincrono: miglioria dichiarata sul v1). fn che ritorna false
//                  (annullata) ⇒ selezione conservata; altrimenti la svuota il
//                  CHIAMANTE (che sa quando l'azione è davvero conclusa).
//   Il click riga IGNORA i controlli interattivi (button/input/a/select/
//   .dt-no-row — parità v1:10950), sempre, anche senza prop nuove.
//   render(row, ctx) riceve ctx = { tokens, Mark } per evidenziare le parti
//   testuali delle celle custom (secondo argomento: i render a 1 argomento
//   esistenti non cambiano).
//
// ESTENSIONE F2.3e (proc-v3 Conversazioni) — dietro prop NUOVA, consumatori
// esistenti INVARIATI:
//   rowClass?(row) → classi extra sul <tr> (parità makeDataTable v1:10905 —
//                  nel monolite evidenzia le conversazioni in entrata). Le
//                  righe con classe propria ricevono anche dt-rowcls: il loro
//                  hover di riga (es. hover:bg-amber-100) resta visibile
//                  perché il fondo hover di default sui td si spegne.
//
// Stile sui token --shell-* (tokens.css.js) con fallback campionati, come
// dash-v3. Primo consumatore reale: la lista Procedure di sop-v3.

import React from 'react';
import {
  selectTableState, sortRows, nextSort, rowKeyOf,
  filterRows, collectTokens, splitByTokens,
} from './data-table-core.js';

const STYLE_ID = 'shell-ui-datatable-style';

function css() {
  return [
    // Fix mobile (segnalazione ondata 12, misurato a 375px): la cella Azioni
    // usciva dal bordo della card SENZA scroll — il 3° bottone era CLIPPATO.
    // CAUSA A MONTE: la CSS globale mobile del monolite (index.html, @media 767px)
    // trasforma OGNI table in display:block con thead/tbody display:table;width:100%
    // → il layout tabellare si sfalda e l'overflow muore clippato. Il monolite ha
    // già la sua via d'uscita (.dt-scroll > table) per le SUE tabelle; il
    // componente condiviso deve essere AUTO-SUFFICIENTE in qualunque documento:
    // (1) il wrapper scorre in orizzontale (mai la pagina);
    // (2) sotto i 768px si RIPRISTINA la tabella vera (display:table, header/row
    //     group) e si cresce a max-content (min-width:100% quando il contenuto è
    //     stretto) — così lo scroll nasce nel wrapper e nulla viene clippato.
    // Desktop INVARIATO (width:100%, testo che va a capo). L'alternativa
    // wrap-della-cella avrebbe richiesto CSS per-consumatore.
    '.dt-wrap{width:100%;overflow-x:auto}',
    '@media(max-width:767px){.dt-table{display:table;width:max-content;min-width:100%}.dt-table thead{display:table-header-group}.dt-table tbody{display:table-row-group}}',
    '.dt-table{width:100%;border-collapse:collapse;background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;overflow:hidden}',
    '.dt-table{border-radius:12px;border-style:hidden;box-shadow:0 0 0 1px #e2e8f0}',
    '.dt-th{text-align:left;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#64748b;padding:10px 14px;border-bottom:1px solid #e2e8f0;background:var(--shell-surface,#fff);white-space:nowrap}',
    '.dt-th.dt-sortable{cursor:pointer;user-select:none}',
    '.dt-th.dt-sortable:hover{color:var(--shell-primary-strong,#0e7490)}',
    '.dt-arrow{font-size:10px;color:var(--shell-primary-strong,#0e7490)}',
    '.dt-td{padding:12px 14px;border-bottom:1px solid #f1f5f9;font-size:14px;color:var(--shell-text,#0f172a);vertical-align:middle}',
    '.dt-tr:last-child .dt-td{border-bottom:0}',
    '.dt-tr.dt-click{cursor:pointer}',
    '.dt-tr.dt-click:hover .dt-td{background:#f8fafc}',
    // F2.3e — righe con rowClass: il fondo hover di default sui td si spegne,
    // così il colore (e l’hover) della classe di riga resta visibile.
    '.dt-tr.dt-click.dt-rowcls:hover .dt-td{background:transparent}',
    '.dt-right{text-align:right}',
    '.dt-center{text-align:center}',
    '.dt-state{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;padding:24px;text-align:center;color:#94a3b8;font-size:13px}',
    '.dt-loading{display:flex;align-items:center;justify-content:center;gap:10px}',
    '.dt-spin{width:18px;height:18px;border-radius:50%;border:2px solid #cffafe;border-top-color:var(--shell-primary,#06b6d4);animation:dt-rot .8s linear infinite;display:inline-block}',
    '@keyframes dt-rot{to{transform:rotate(360deg)}}',
    '.dt-error{border-color:#fecaca;background:#fef2f2}',
    '.dt-errmsg{color:#b91c1c;font-size:13px;font-weight:600}',
    '.dt-retry{margin-top:12px;background:var(--shell-primary-strong,#0e7490);color:#fff;border:0;border-radius:8px;padding:8px 16px;font-weight:600;cursor:pointer;font-size:13px}',
    // F2.3c — filtri per colonna, evidenziazione, selezione, footer conteggio
    '.dt-fth{padding:6px 8px;border-bottom:1px solid #e2e8f0;background:#f8fafc}',
    '.dt-filter{width:100%;box-sizing:border-box;border:1px solid #e2e8f0;border-radius:6px;padding:3px 8px;font-size:12px;background:var(--shell-surface,#fff);color:var(--shell-text,#0f172a)}',
    '.dt-filter:focus{outline:none;border-color:var(--shell-primary,#06b6d4)}',
    '.dt-mark{background:#fef08a;color:#713f12;padding:0 2px;border-radius:3px}',
    '.dt-selth{width:32px;padding:10px 8px;border-bottom:1px solid #e2e8f0;background:var(--shell-surface,#fff);text-align:center}',
    '.dt-seltd{width:32px;padding:12px 8px;border-bottom:1px solid #f1f5f9;text-align:center;vertical-align:middle}',
    '.dt-bulkbar{display:flex;align-items:center;flex-wrap:wrap;gap:8px;margin-bottom:8px;padding:8px 10px;background:#ecfeff;border:1px solid #a5f3fc;border-radius:10px;font-size:13px}',
    '.dt-bulkn{font-weight:700;color:#155e75}',
    '.dt-bulkbtn{background:var(--shell-surface,#fff);border:1px solid #67e8f9;color:#0e7490;border-radius:7px;padding:4px 10px;font-size:12px;font-weight:600;cursor:pointer}',
    '.dt-bulkbtn:hover{background:#cffafe}',
    '.dt-bulkbtn:disabled{opacity:.5;cursor:default}',
    '.dt-bulkclear{margin-left:auto;background:transparent;border:0;color:#64748b;font-size:12px;cursor:pointer}',
    '.dt-bulkclear:hover{color:var(--shell-text,#0f172a)}',
    '.dt-empty-row{padding:24px 14px;text-align:center;color:#94a3b8;font-style:italic;font-size:13px}',
    '.dt-count{margin-top:8px;font-size:12px;color:#64748b}',
  ].join('\n');
}

export function injectDataTableStyle(doc) {
  if (!doc || doc.getElementById(STYLE_ID)) return;
  const st = doc.createElement('style');
  st.id = STYLE_ID;
  st.textContent = css();
  doc.head.appendChild(st);
}

// Evidenziatore condiviso: le parti che matchano i token finiscono in <mark>
// (classe dt-mark). Per le celle piane lo applica il DataTable; i render custom
// lo ricevono in ctx.Mark e lo applicano alle loro parti TESTUALI.
export function Mark({ text, tokens }) {
  const parts = splitByTokens(text, tokens);
  if (parts.length === 1 && !parts[0].hit) return <>{parts[0].text}</>;
  return (
    <>
      {parts.map((p, i) => (p.hit ? <mark key={i} className="dt-mark">{p.text}</mark> : <React.Fragment key={i}>{p.text}</React.Fragment>))}
    </>
  );
}

// guardia click-riga: mai aprire la riga cliccando un controllo interattivo
// (checkbox di selezione, bottoni, link, select — parità v1 proc-v0.js:10950)
function isInteractiveTarget(t) {
  return !!(t && typeof t.closest === 'function' && t.closest('button, input, a, select, .dt-no-row'));
}

export function DataTable(props) {
  const {
    columns, rows, rowKey, onRowClick, sort, loading, error, onRetry, emptyMessage,
    filterable, selectable, selection, bulkActions, resolveSelection, onBulkDone,
    rowClass,
  } = props;
  const list = rows || [];
  const cols = columns || [];
  const [sortState, setSortState] = React.useState(sort || null);
  const [filters, setFilters] = React.useState({});
  const [, setSelTick] = React.useState(0); // la selezione vive nel Set esterno: qui solo il repaint
  const busyRef = React.useRef(false);      // guardia SINCRONA anti doppio-click bulk (setState è async)
  const [busy, setBusy] = React.useState(false);
  const wrapRef = React.useRef(null);

  // stile una volta per documento, dal documento REALE in cui il componente vive
  React.useEffect(() => {
    const el = wrapRef.current;
    if (el && el.ownerDocument) injectDataTableStyle(el.ownerDocument);
  }, []);

  const sel = (selectable && selection instanceof Set) ? selection : null;
  const bumpSel = () => setSelTick((n) => n + 1);

  const state = selectTableState({ loading, error, rows: list });
  let body;
  let bulkBar = null;
  let footer = null;
  if (state === 'loading') {
    body = (
      <div className="dt-state dt-loading" role="status">
        <span className="dt-spin" />
        <span>Caricamento…</span>
      </div>
    );
  } else if (state === 'error') {
    const msg = error && error.message ? error.message : String(error);
    body = (
      <div className="dt-state dt-error" role="alert">
        <div className="dt-errmsg">{'⚠ ' + msg}</div>
        {typeof onRetry === 'function' && (
          <button type="button" className="dt-retry" onClick={() => onRetry()}>Riprova</button>
        )}
      </div>
    );
  } else if (state === 'empty' && !filterable) {
    // box vuoto dedicato del design system (consumatori storici: sop-v3)
    body = <div className="dt-state dt-empty">{emptyMessage || 'Nessun elemento.'}</div>;
  } else {
    // Con filterable anche lo stato VUOTO passa di qui (parità makeDataTable v1,
    // provata dal collaudo f23c: filtro alto a 0 risultati ⇒ il v1 mostra la
    // tabella con riga-messaggio + footer "0 di 0 righe." e i filtri colonna
    // restano usabili — mai un box che ingoia footer e filtri).
    const filtered = filterable ? filterRows(list, filters, cols) : list;
    const sorted = sortRows(filtered, sortState, cols);
    const tokensByCol = {};
    if (filterable) for (const c of cols) tokensByCol[c.key] = collectTokens(filters[c.key]);
    const alignCls = (c) => (c.align === 'right' ? ' dt-right' : c.align === 'center' ? ' dt-center' : '');
    const nCols = cols.length + (sel ? 1 : 0);
    const allFilteredSelected = !!sel && filtered.length > 0 && filtered.every((r) => sel.has(r && r.id != null ? String(r.id) : ''));

    // barra bulk (parità v1:10911): appare con selezione non vuota
    if (sel && sel.size > 0) {
      const runBulk = async (action) => {
        if (busyRef.current) return; // doppio click = 1 sola esecuzione
        const ids = Array.from(sel);
        const chosen = typeof resolveSelection === 'function'
          ? resolveSelection(ids)
          : list.filter((r) => sel.has(r && r.id != null ? String(r.id) : ''));
        if (!action || typeof action.fn !== 'function' || !chosen.length) return;
        busyRef.current = true; setBusy(true);
        let res;
        try {
          res = await action.fn(chosen);
        } finally {
          busyRef.current = false; setBusy(false);
        }
        // false = annullata ⇒ selezione conservata (parità v1:10976); altrimenti
        // decide il CHIAMANTE (onBulkDone), che sa quando l'azione è conclusa.
        if (res !== false && typeof onBulkDone === 'function') onBulkDone(action, chosen);
        bumpSel();
      };
      bulkBar = (
        <div className="dt-bulkbar">
          <span className="dt-bulkn">{sel.size} selezionati</span>
          {(bulkActions || []).map((a, i) => (
            <button key={i} type="button" className="dt-bulkbtn" disabled={busy} onClick={() => runBulk(a)}>{a.label}</button>
          ))}
          <button type="button" className="dt-bulkclear" disabled={busy} onClick={() => { sel.clear(); bumpSel(); }}>Deseleziona</button>
        </div>
      );
    }

    body = (
      <table className="dt-table">
        <thead>
          <tr>
            {sel && (
              <th className="dt-selth">
                <input
                  type="checkbox"
                  title="Seleziona/deseleziona tutti i filtrati"
                  checked={allFilteredSelected}
                  onChange={() => {
                    if (allFilteredSelected) filtered.forEach((r) => sel.delete(r && r.id != null ? String(r.id) : ''));
                    else filtered.forEach((r) => sel.add(r && r.id != null ? String(r.id) : ''));
                    bumpSel();
                  }}
                />
              </th>
            )}
            {cols.map((c) => (
              <th
                key={c.key}
                className={'dt-th' + alignCls(c) + (c.sortable ? ' dt-sortable' : '')}
                onClick={c.sortable ? () => setSortState((s) => nextSort(s, c.key)) : undefined}
              >
                {c.label}
                {c.sortable && sortState && sortState.key === c.key
                  ? <span className="dt-arrow">{sortState.dir === 'desc' ? ' ▼' : ' ▲'}</span>
                  : null}
              </th>
            ))}
          </tr>
          {filterable && (
            <tr>
              {sel && <th className="dt-fth" />}
              {cols.map((c) => (
                <th key={c.key} className="dt-fth">
                  {c.filter === false ? null : (
                    <input
                      type="text"
                      className="dt-filter"
                      data-filter-col={c.key}
                      placeholder="Filtra ( , = oppure )…"
                      title="Più parole = tutte richieste (E). Usa la virgola per più valori: es. 'metalli, viti' mostra l'uno OPPURE l'altro."
                      value={filters[c.key] || ''}
                      onChange={(e) => { const v = e.target.value; setFilters((f) => ({ ...f, [c.key]: v })); }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td className="dt-empty-row" colSpan={nCols}>{emptyMessage || 'Nessun elemento.'}</td>
            </tr>
          ) : sorted.map((row, i) => {
            const id = row && row.id != null ? String(row.id) : '';
            const extraCls = typeof rowClass === 'function' ? (rowClass(row) || '') : '';
            return (
              <tr
                key={rowKeyOf(row, rowKey, i)}
                className={'dt-tr' + (onRowClick ? ' dt-click' : '') + (extraCls ? ' dt-rowcls ' + extraCls : '')}
                onClick={onRowClick ? (e) => { if (isInteractiveTarget(e.target)) return; onRowClick(row); } : undefined}
              >
                {sel && (
                  <td className="dt-seltd">
                    <input
                      type="checkbox"
                      checked={sel.has(id)}
                      onChange={() => { if (sel.has(id)) sel.delete(id); else sel.add(id); bumpSel(); }}
                    />
                  </td>
                )}
                {cols.map((c) => {
                  const tokens = (filterable && tokensByCol[c.key] && tokensByCol[c.key].length) ? tokensByCol[c.key] : null;
                  let cell;
                  if (typeof c.render === 'function') {
                    cell = c.render(row, { tokens: tokens || [], Mark });
                  } else {
                    const raw = row == null || row[c.key] == null ? '' : String(row[c.key]);
                    cell = tokens ? <Mark text={raw} tokens={tokens} /> : raw;
                  }
                  return <td key={c.key} className={'dt-td' + alignCls(c)}>{cell}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
    if (filterable) {
      footer = (
        <div className="dt-count">
          {filtered.length + ' di ' + list.length + ' ' + (list.length === 1 ? 'riga' : 'righe') + '.'}
        </div>
      );
    }
  }
  return (
    <div className="dt-wrap" ref={wrapRef}>
      {bulkBar}
      {body}
      {footer}
    </div>
  );
}
